import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, initialUrlType } from '../lib/supabase';
import { clearIdleMarker, idleExpired } from './useIdleTimeout';
import { releaseDevice as releasePushDevice } from '../lib/push';

const AuthContext = createContext(null);

// Two retries at 0.8s and 1.6s, comfortably inside the 10s safety timer below.
const PROFILE_FETCH_RETRIES = 2;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  // Set only once the retries are exhausted, so the app can tell "still loading"
  // apart from "signed in, but the profile genuinely will not load".
  const [profileError, setProfileError] = useState(false);
  const [loading, setLoading] = useState(true);
  // An invite or recovery link is known to be a set-password visit from the URL
  // alone, so decide it before the first render. Waiting for the auth event let
  // the dashboard paint for a moment first — the client saw their exercises
  // flash up before the password screen replaced them.
  const isPasswordLink = initialUrlType === 'invite' || initialUrlType === 'recovery';
  const [needsPasswordReset, setNeedsPasswordReset] = useState(isPasswordLink);
  // 'invite' | 'recovery' | 'weak' — why the set-password screen is showing, so it
  // can explain itself rather than appearing unprompted after a correct login.
  const [passwordResetReason, setPasswordResetReason] = useState(isPasswordLink ? initialUrlType : null);

  // When the user returns to the tab after a long idle, the access token may have
  // expired while Supabase's autoRefresh timer was throttled by the browser.
  // Proactively clear auth state so the login page appears immediately instead of
  // leaving the app stuck on a spinner waiting for a queued token-refresh to resolve.
  useEffect(() => {
    async function handleVisibility() {
      if (document.visibilityState !== 'visible') return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setUser(null);
        setProfile(null);
        setProfileError(false);
        setLoading(false);
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    // If token refresh or profile fetch hangs (e.g. stale session + network issue),
    // force loading to false so the login screen appears instead of an infinite spinner.
    const safetyTimer = setTimeout(() => {
      setLoading(false);
    }, 10000);

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        // A remembered session survives the app being closed, so the idle limit
        // has to be judged before anything is shown: without this, launching the
        // app a day later restored the dashboard and the timeout only started
        // counting from that moment.
        // Not on an invite or recovery visit: that session was created seconds
        // ago by the link itself, and signing it out would strand the user on a
        // password screen they can no longer use.
        if (!isPasswordLink && session?.user && idleExpired()) {
          signOut().finally(() => setLoading(false));
          return;
        }
        setUser(session?.user ?? null);
        if (session?.user) fetchProfile(session.user.id);
        else setLoading(false);
      })
      .catch(() => {
        setUser(null);
        setLoading(false);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'INITIAL_SESSION') return;

        setUser(session?.user ?? null);
        // PASSWORD_RECOVERY = password reset link clicked.
        // SIGNED_IN + type=invite = new client clicking their invite link.
        // Both need the set-password screen, and both must clear loading so the
        // safety timer isn't the only thing unblocking the spinner.
        if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && initialUrlType === 'invite')) {
          setNeedsPasswordReset(true);
          setPasswordResetReason(event === 'PASSWORD_RECOVERY' ? 'recovery' : 'invite');
          setLoading(false);
          return;
        }
        if (session?.user) {
          fetchProfile(session.user.id); // intentionally not awaited — avoids deadlock with Supabase auth client
        } else {
          setProfile(null);
          setProfileError(false);
          setLoading(false);
        }
      }
    );

    return () => {
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  // A failed profile fetch used to leave `profile` null, and the router reads
  // "signed in but no profile" as signed out, so a dropped connection on clinic
  // wifi looked like the app had logged the client out at random. Retry the
  // blip, and if it really cannot be read, say so instead of pretending.
  async function fetchProfile(userId, attempt = 0) {
    let row = null;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      row = data;
    } catch (err) {
      console.error('Profile fetch failed:', err);
      row = null;
    }

    if (row) {
      setProfile(row);
      setProfileError(false);
      setLoading(false);
      return;
    }

    if (attempt < PROFILE_FETCH_RETRIES) {
      setTimeout(() => fetchProfile(userId, attempt + 1), 800 * (attempt + 1));
      return;
    }

    setProfile(null);
    setProfileError(true);
    setLoading(false);
  }

  async function signIn(email, password) {
    // Start this login's idle clock from nothing. A marker left behind by
    // whoever used the device last would otherwise be read as this session's
    // own quiet time and sign the new user straight back out.
    clearIdleMarker();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      // AuthWeakPasswordError: password found in a breach database, but login succeeded.
      // Authenticate the user and send them straight to the password-change screen.
      if (error.name === 'AuthWeakPasswordError' && data?.session) {
        setNeedsPasswordReset(true);
        setPasswordResetReason('weak');
        return data;
      }
      throw error;
    }
    return data;
  }

  // releaseDevice is for the sign-out buttons only. An idle or expired session
  // also signs out, and turning reminders off then would silence them for the
  // very clients who haven't opened the app in a while.
  async function signOut({ releaseDevice = false } = {}) {
    if (releaseDevice) {
      try {
        await Promise.race([releasePushDevice(), new Promise(resolve => setTimeout(resolve, 3000))]);
      } catch {
        // Never hold up sign-out over reminders.
      }
    }
    try {
      await supabase.auth.signOut();
    } catch {
      // server-side revocation failed; still clear local session
    }
    // scope:'local' always wipes sessionStorage regardless of server response
    try { await supabase.auth.signOut({ scope: 'local' }); } catch {}
    // Otherwise the next person to sign in on this device inherits the previous
    // user's idle clock and can be signed straight back out.
    clearIdleMarker();
    setUser(null);
    setProfile(null);
    setProfileError(false);
  }

  async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  }

  const isTherapist = profile?.role === 'therapist';
  const isClient = profile?.role === 'client';
  const isLead = isTherapist && profile?.is_lead === true;

  function clearPasswordReset() {
    setNeedsPasswordReset(false);
    setPasswordResetReason(null);
  }

  return (
    <AuthContext.Provider value={{
      user, profile, loading, profileError,
      isTherapist, isClient, isLead,
      needsPasswordReset, passwordResetReason, clearPasswordReset,
      signIn, signOut, resetPassword,
      refreshProfile: () => user && fetchProfile(user.id),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
